import React from 'react';
import { useEffect, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Typography, Grid2, useTheme, Skeleton } from '@mui/material';
import { Credits } from 'react-jsx-highcharts';
import { useGetScheduleProgressCurveQuery } from "store/slices/apis/scheduleDashboarSlice";
import { useParams } from "react-router-dom";
import moment from "moment";

const ScurveChart = () => {
    const theme = useTheme();
    const { projectId } = useParams();
    const { data, isLoading } = useGetScheduleProgressCurveQuery({ projectId });

    const categories = useMemo(() => {
        return data?.filter(d => d?.month !== null)?.map(d => moment(d.month, "MMM-YYYY").format("MMM-YY")) || [];
    }, [data]);

    const actualData = useMemo(() => data?.map(d => d?.actual ?? null) || [], [data]);
    const planData = useMemo(() => data?.map(d => d?.plan ?? null) || [], [data]);
    const revisedData = useMemo(() => data?.map(d => d?.forecast ?? null) || [], [data]);

    const { actualTotal, planTotal } = useMemo(() => {
        let actualTotal = 0;
        let planTotal = 0;
        const today = moment();

        if (!data) return { actualTotal, planTotal };

        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            const month = moment(item.month, "MMM-YYYY");

            if (month.isSameOrBefore(today, 'month')) {
                actualTotal += item.actual || 0;
                planTotal += item.plan || 0;
            }
        }

        return { actualTotal, planTotal };
    }, [data]);

    const actualLineColor = actualTotal >= planTotal ? '#3E756D' : '#E72B33';
    const options = {
        colors: ["#0840DE", "#415385", "#3E756D"],
        title: { text: '' },
        xAxis: {
            title: { text: 'Months' },
            categories: categories,
            tickLength: 10, // लाइन की लंबाई
            tickWidth: 1,   // लाइन की चौड़ाई
            tickColor: '#999', // लाइन का रंग
            lineWidth: 1,   // X-axis की लाइन
            lineColor: '#ccc',
            labels: {
                style: { color: '#182A4E', }
            },

        },
        yAxis: {
            title: { text: 'Progress Percentage' },
            tickPositions: [0, 20, 40, 60, 80, 100],
            labels: {
                style: { color: '#182A4E' }
            },
            min: 0,
            gridLineWidth: 1,
            lineWidth: 1,
            lineColor: '#ccc'
        },
        legend: {
            align: "left",
            verticalAlign: "top",
            layout: "horizontal",
            itemDistance: 20,
            useHTML: true,
            symbolWidth: 0,
            x: -10,
            itemMarginBottom: 20,
            labelFormatter: function () {
                return `<span style="display:inline-block;width:13px;height:13px;background-color:${this.color};margin-right:5px;"></span>
              <span style="font-size:15px;font-weight:500;color:#00000080">${this.name}</span>`;
            }
        },
        tooltip: {
            shared: true,
            useHTML: true,
            backgroundColor: "#00000080",
            shadow: false,
            formatter: function () {
                const plan = this.points.find(p => p.series.name === 'Planned')?.y ?? 0;
                const forecast = this.points.find(p => p.series.name === 'Forecast')?.y ?? 0;
                const actual = this.points.find(p => p.series.name === 'Actual')?.y ?? 0;

                return `
           <b style="color:#fff">Planned: ${plan}</b><br/>
        <b style="color:#fff">Actual: ${actual}</b><br/>
        <b style="color:#fff">Forecast: ${forecast}</b>`
            },
            style: { color: '#fff' }
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                    states: {
                        hover: {
                            enabled: true,
                            radius: 5
                        }
                    }
                },
                states: {
                    hover: {
                        lineWidthPlus: 0
                    }
                }
            }
        },
        series: [
            {
                name: 'Planned',
                data: planData,
                color: '#295477',
                lineWidth: 2
            },
            {
                name: 'Actual',
                data: actualData,
                color: actualLineColor,
                lineWidth: 2
            },
            {
                name: 'Forecast',
                data: revisedData,
                color: '#ffa500',
                dashStyle: 'Dash',
                lineWidth: 2
            },
        ],
        credits: {
            enabled: false
        }
    };

    const hasValidData = useMemo(
        () =>
            data?.some(
                (d) =>
                    d?.actual !== null ||
                    d?.plan !== null ||
                    d?.forecast !== null ||
                    d?.month !== null
            ),
        [data]
    );

    return (
        <>
            <Grid2 size={{ xs: 12, md: 12 }} sx={{ mb: 1, p: 2 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: "500",
                        fontSize: '18px',
                        color: `${theme?.customSettings?.themeColor}`,
                        lineHeight: '100%',
                        letterSpacing: '-0.4px',

                    }}
                >
                    Physical Progress - S Curve
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: "400",
                        fontSize: '14px',
                        color: '#182A4E80',
                        lineHeight: '100%',
                        letterSpacing: '-0.4px',


                    }}
                >
                    Project Performance and trends over time
                </Typography>
            </Grid2>
            <Grid2 size={{ md: 12 }}>
                {isLoading ? (
                    <>
                        <Box height={400}>
                            <Skeleton variant="rectangular" height={390} animation="wave" sx={{ borderRadius: 7 }} />
                        </Box>
                    </>
                ) : !hasValidData ? (
                    <Box sx={{ padding: 5, textAlign: "center", color: "#888", minHeight: "400px" }}>
                        No data available to display the chart.
                    </Box>
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={options} />
                )}
            </Grid2>
        </>
    );
};

export default ScurveChart;
